import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'next-i18next';
import {useRouter} from 'next/router';
import {Box, IconButton, SearchInput} from '@tempo/core';
import {SearchOutlinedIcon} from '@tempo/icons';

import SearchMode from '@deezer/next/shared/utils/SearchMode';
import SearchModal from './SearchModal';
import {useUser} from '@deezer/next/contexts/main/hooks';
import {FeatureLabel} from '@deezer/next/services/types/features';

type SearchBarProps = {
	collapsed?: boolean;
	searchMode: SearchMode;
};

// TODO redo this component so that it can be reused elsewhere
const SearchBar = ({searchMode}: SearchBarProps) => {
	const {t} = useTranslation('d4c');
	const user = useUser();
	const router = useRouter();
	const hasSearchRight = useMemo(() => {
		if (!user) {
			return false;
		}
		if (searchMode === SearchMode.analytic) {
			return user.features.some(
				f => f.label === FeatureLabel.ANALYTICS_VIEWER && f.enabled,
			);
		}
		if (searchMode === SearchMode.editArtist) {
			return user.features.some(
				f => f.label === FeatureLabel.ARTISTS_EDITOR && f.enabled,
			);
		}
		return true;
	}, [searchMode, user]);

	const [isSearchVisible, setIsSearchVisible] = useState(false);

	// Callbacks
	const onHide = useCallback(() => {
		setIsSearchVisible(false);
	}, []);

	const onButtonClick = useCallback(() => {
		setIsSearchVisible(true);
	}, []);

	const searchPlaceholder = useMemo(() => {
		return searchMode === SearchMode.analytic
			? t('analyticshome_text_searchanalytics_d4c-web')
			: t('analyticsartist_text_searchartists_d4c-web');
	}, [searchMode, t]);

	// Close on route change
	useEffect(() => {
		(() => {
			if (isSearchVisible) {
				onHide();
			}
		})();
		// We want to hide the search when we change route - a.k.a. select a result
	}, [router.asPath]);

	if (!hasSearchRight) {
		return null;
	}

	return (
		<>
			<IconButton
				display={{base: 'flex', md: 'none'}}
				data-testid="open-search-button-icon"
				aria-label={searchMode}
				onClick={onButtonClick}
				variant="outline"
				size="small"
				icon={<SearchOutlinedIcon />}
			/>
			<Box display={{base: 'none', md: 'block'}}>
				<SearchInput
					data-testid="open-search-button"
					onClick={onButtonClick}
					// Disable autofocus on input click
					onFocus={e => e.currentTarget.blur()}
					placeholder={searchPlaceholder}
					cursor="pointer"
					size="small"
				/>
			</Box>
			<SearchModal
				isVisible={isSearchVisible}
				onClose={onHide}
				searchMode={searchMode}
				searchPlaceholder={searchPlaceholder}
			/>
		</>
	);
};

export default SearchBar;
